import React from 'react';

import { EM_DASH, formatPercent, TimeReportDays, TimeReportMoney } from './timeReportFormat';

/**
 * Mirrors CashflowKpiTile in organisations/summary: a title, a large value, a
 * free-text context line and a sign-based colour. Deliberately not
 * CommonComponents/KeyStatTile, which models a ±% variance with a triangle —
 * none of these four figures is a variance.
 */
export function TimeReportKpiTile({ title, value, valueClassName, context, children }) {
    return (
        <div className="tw-px-4 tw-py-3 tw-rounded-lg tw-border-2 tw-border-grey">
            <div className="tw-text-sm tw-text-blue-75 tw-font-medium tw-mb-2">{title}</div>
            <div className={`tw-text-2.5xl tw-font-semibold ${valueClassName || 'tw-text-charcoal'}`}>{value}</div>
            {context && <div className="tw-text-xs tw-text-charcoal-50 tw-mt-1">{context}</div>}
            {children}
        </div>
    );
}

/**
 * The four headline figures. They follow the active filters exactly — with Type
 * set to Internal the freelance share reads zero, because inside that filter it
 * is zero.
 */
export default function TimeReportKpis({ kpis, currency }) {
    if (!kpis) {
        return (
            <div className="tw-grid tw-grid-cols-4 tw-gap-x-5 tw-gap-y-3">
                {[0, 1, 2, 3].map((index) => (
                    <TimeReportKpiTile key={index} title={EM_DASH} value={EM_DASH} />
                ))}
            </div>
        );
    }

    // A negative margin is not a success, so the colour follows the sign rather
    // than being fixed green.
    const marginColour = kpis.margin < 0 ? 'tw-text-red' : 'tw-text-green';

    const noBilledValue = kpis.margin_percent === null && kpis.billed_value === 0;

    const shareContext = (percent) => (noBilledValue ? 'Nothing charged to measure against' : `${formatPercent(percent, 1)} of charged`);

    const marginContext = noBilledValue
        ? 'Nothing charged to measure against'
        : `${formatPercent(kpis.margin_percent, 1)} of charged, ${formatPercent(kpis.markup_percent, 1)} markup on cost to date`;

    return (
        <div className="tw-grid tw-grid-cols-4 tw-gap-x-5 tw-gap-y-3">
            <TimeReportKpiTile title="Cost to date" value={<TimeReportMoney value={kpis.time_cost} currency={currency} />} context="rate card cost rates" />
            <TimeReportKpiTile
                title="Charged"
                value={<TimeReportMoney value={kpis.billed_value} currency={currency} />}
                context={
                    <>
                        <TimeReportDays value={kpis.days} /> {$t('days recorded')}
                    </>
                }
            />
            <TimeReportKpiTile
                title="Margin"
                value={<TimeReportMoney value={kpis.margin} currency={currency} />}
                valueClassName={marginColour}
                context={marginContext}
            />
            <TimeReportKpiTile
                title={$t('Freelance share')}
                value={<TimeReportMoney value={kpis.freelance_value} currency={currency} />}
                context={shareContext(kpis.freelance_percent)}
            />
        </div>
    );
}
